import React, { useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer } from "lucide-react";
import PixQrCode from "@/components/PixQrCode";

export const PAYMENT_LABELS = { cash: "Dinheiro", credit: "Crédito", debit: "Débito", pix: "PIX", credit_term: "Prazo/Promissória" };

const safeLogo = (u) => (/^(https?:|data:image\/)/i.test(String(u || "").trim()) ? u : null);

const ReceiptHeader = ({ company, sale }) => (
  <div className="text-center border-b border-dashed border-slate-400 pb-2 mb-2 space-y-1">
    {safeLogo(company.logo_url) && (
      <img src={safeLogo(company.logo_url)} alt="Logo" className="mx-auto max-h-16 max-w-[160px] object-contain" data-testid="receipt-logo" />
    )}
    <div className="font-bold text-base" data-testid="receipt-company-name">{company.name || "NEXUS PDV"}</div>
    {company.cnpj && <div className="text-xs" data-testid="receipt-cnpj">CNPJ: {company.cnpj}</div>}
    {company.address && <div className="text-[11px] text-slate-600">{company.address}</div>}
    {(company.phone || company.email) && (
      <div className="text-[11px] text-slate-600">{[company.phone, company.email].filter(Boolean).join(" • ")}</div>
    )}
    <div className="text-xs pt-1">Venda #{sale.id.slice(-8).toUpperCase()} — {new Date(sale.created_at).toLocaleString("pt-BR")}</div>
    {sale.customer && <div className="text-xs">Cliente: {sale.customer}</div>}
    {sale.status === "cancelled" && <div className="text-xs font-bold text-rose-600">VENDA CANCELADA</div>}
  </div>
);

const ReceiptItems = ({ items }) => (
  <table className="w-full text-xs" data-testid="receipt-items">
    <thead>
      <tr className="border-b border-slate-300">
        <th className="text-left py-1">Item</th>
        <th className="text-right py-1">Qtd</th>
        <th className="text-right py-1">Unit.</th>
        <th className="text-right py-1">Total</th>
      </tr>
    </thead>
    <tbody>
      {items.map((i, idx) => (
        <tr key={`${i.product_id || i.name}-${idx}`} className="border-b border-slate-100">
          <td className="py-1 pr-1">{i.name}</td>
          <td className="text-right py-1">{i.quantity}</td>
          <td className="text-right py-1">{brl(i.price)}</td>
          <td className="text-right py-1">{brl(i.price * i.quantity)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ReceiptTotals = ({ sale }) => (
  <div className="border-t border-dashed border-slate-400 mt-2 pt-2 space-y-1">
    <div className="flex justify-between"><span>Subtotal</span><span>{brl(sale.subtotal)}</span></div>
    <div className="flex justify-between"><span>Desconto</span><span>-{brl(sale.discount)}</span></div>
    <div className="flex justify-between font-bold text-base"><span>TOTAL</span><span data-testid="receipt-total">{brl(sale.total)}</span></div>
    <div className="text-xs text-slate-600">Pagamento: {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}</div>
    {sale.cash_received > 0 && <div className="text-xs">Recebido: {brl(sale.cash_received)}</div>}
    {sale.change > 0 && <div className="text-xs">Troco: {brl(sale.change)}</div>}
    {sale.payment_method === "credit_term" && sale.due_date && (
      <div className="text-xs">Vencimento: {sale.due_date} ({sale.installments || 1}x)</div>
    )}
  </div>
);

const SignatureBlock = () => (
  <div className="pt-10 mt-4" data-testid="receipt-signature">
    <div className="border-t border-slate-800 mx-4" />
    <div className="text-center text-xs mt-1">Assinatura / Carimbo</div>
  </div>
);

export const SaleReceipt = ({ sale, company }) => (
  <div className="print-area font-mono text-sm bg-white text-slate-900" data-testid="sale-receipt">
    <ReceiptHeader company={company} sale={sale} />
    <ReceiptItems items={sale.items} />
    <ReceiptTotals sale={sale} />
    {sale.payment_method === "pix" && sale.status !== "cancelled" && (
      <div className="border-t pt-3 mt-3 no-print">
        <PixQrCode amount={sale.total} txid={"NEXUS" + sale.id.slice(-10).toUpperCase()} />
      </div>
    )}
    <SignatureBlock />
    <div className="text-center text-[10px] text-slate-500 mt-3">Obrigado pela preferência!</div>
  </div>
);

export const ReceiptDialog = ({ sale, onClose, title = "Comprovante de Venda" }) => {
  const [company, setCompany] = useState({});
  useEffect(() => {
    if (sale) { api.get("/company").then((r) => setCompany(r.data || {})).catch(() => setCompany({})); }
  }, [sale]);
  return (
    <Dialog open={!!sale} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="receipt-modal">
        <DialogHeader className="no-print"><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {sale && <SaleReceipt sale={sale} company={company} />}
        <DialogFooter className="no-print">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-receipt-btn"><Printer className="w-4 h-4 mr-2" /> Imprimir</Button>
          <Button onClick={onClose} data-testid="close-receipt-btn">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
